import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './NewsPage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import newsItemsFallback from '../data/news';
import { fetchNewsPageItems } from '../contentful/newsApi';
import { isContentfulConfigured } from '../contentful/client';

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const NewsCardBody = ({ item }) => (
  <>
    <div className={`${styles.sourceLogo} ${styles.sourceLogoTop}`}>
      {item.agencyLogoUrl ? (
        <img
          src={item.agencyLogoUrl}
          alt={item.publication || 'News outlet'}
          className={styles.agencyLogoImg}
        />
      ) : (
        <span className={styles.logoDefault}>{item.publication}</span>
      )}
    </div>
    {item.representativeImageUrl ? (
      <img
        src={item.representativeImageUrl}
        alt=""
        className={styles.representativeImage}
      />
    ) : null}
    <h2
      className={styles.newsTitle}
      style={{ fontFamily: "'Jost', sans-serif", fontSize: '20px', fontStyle: 'normal', fontWeight: 400, lineHeight: '28px', color: colors.dark }}
    >
      {item.title}
    </h2>
    {item.publication && (
      <p
        className={styles.newsAgency}
        style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 400, lineHeight: '26px', color: 'var(--Secondary-Text-Color, #6B7280)' }}
      >
        {item.publication}
      </p>
    )}
    <p
      className={styles.newsMeta}
      style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 400, lineHeight: '26px', color: 'var(--Secondary-Text-Color, #6B7280)' }}
    >
      {item.date}
    </p>
  </>
);

const NewsCard = ({ item }) => {
  if (isHttpUrl(item.link)) {
    return (
      <a
        href={item.link.trim()}
        className={styles.newsCard}
        target="_blank"
        rel="noopener noreferrer"
      >
        <NewsCardBody item={item} />
      </a>
    );
  }
  return (
    <div className={styles.newsCard}>
      <NewsCardBody item={item} />
    </div>
  );
};

const NewsPage = () => {
  usePageMeta(pagesSeo.news);
  const [items, setItems] = useState(() =>
    isContentfulConfigured() ? null : newsItemsFallback
  );
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }

    let cancelled = false;

    fetchNewsPageItems()
      .then((data) => {
        if (!cancelled) {
          setItems(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err?.message || 'Could not load news from Contentful.'
          );
          setItems([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const showLoading = items === null;
  const list = items ?? [];

  return (
    <div>
      <Header />
      <section className={styles.newsSection}>
        <div className={styles.container}>
          <h1 className={`${styles.pageTitle} page-primary-title`} style={{ ...typography.heading.large, color: colors.dark }}>
            DAI LAB IN THE NEWS
          </h1>
          {loadError && (
            <p
              className={styles.loadError}
              style={{ ...typography.body.medium.regular, color: colors.gray[900] }}
              role="alert"
            >
              {loadError}
            </p>
          )}
          <hr className={styles.divider} />

          {showLoading ? (
            <p
              style={{ ...typography.body.large.regular, color: colors.gray[900] }}
            >
              Loading news…
            </p>
          ) : (
            <div className={styles.newsGrid}>
              {list.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default NewsPage;
